import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Container from '@mui/joy/Container';
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import { DroneProfile } from '../models/DroneProfile/DroneProfile.model';
import { DroneProfileService } from '../services/DroneProfileService';
import HeartBeat from '../components/HeartBeat/HeartBeat';
import BreadcrumbLayout from '../components/layout/BreadcrumbLayout';
import { BreadcrumbItem } from '../models/BreadcrumbItem';
import ResponsiveLayout from '../components/layout/ResponsiveLayout';

const DroneDetails = () => {
  const { t } = useTranslation();
  const { droneId } = useParams();
  const navigate = useNavigate();
  const [drone, setDrone] = useState<DroneProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!droneId) return;
    setLoading(true);
    DroneProfileService.getDroneProfileById(droneId as string)
      .then((res) => {
        if (res.success && res.data && res.data) {
          setDrone(res.data);
        } else {
          setError(t('drones.error_fetch'));
        }
      })
      .catch(() => setError(t('drones.error_fetch')))
      .finally(() => setLoading(false));
  }, [droneId, t]);

  if (!droneId)
    return (
      <Alert color="danger" variant="soft">
        {t('drones.table.name')} {t('not_found')}
      </Alert>
    );
  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}><CircularProgress /></Box>;
  if (error) return <Alert color="danger" variant="soft">{error}</Alert>;
  if (!drone)
    return (
      <Alert color="danger" variant="soft">
        {t('drones.table.name')} {droneId} {t('not_found')}
      </Alert>
    );

  // Setup breadcrumbs
  const breadcrumbItems: BreadcrumbItem[] = [
    { text: t('drones.title'), link: '/drones' },
    { text: drone.name || droneId },
  ];

  return (
    <ResponsiveLayout>
      <BreadcrumbLayout breadcrumbItems={breadcrumbItems}>
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>

            <Typography level="h2">{t('drone.name')}: {drone.name}</Typography>
          </Stack>
          <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
            <Table size="sm" borderAxis="xBetween">
              <tbody>
                <tr>
                  <td><Typography level="body-md" fontWeight="lg">{t('drone.eid')}</Typography></td>
                  <td>{drone.eid}</td>
                </tr>
                <tr>
                  <td><Typography level="body-md" fontWeight="lg">{t('drone.model')}</Typography></td>
                  <td>{drone.drone_model_name}</td>
                </tr>
                <tr>
                  <td><Typography level="body-md" fontWeight="lg">{t('drone.created')}</Typography></td>
                  <td>{drone.createdAt ? new Date(drone.createdAt).toLocaleString() : '-'}</td>
                </tr>
                <tr>
                  <td><Typography level="body-md" fontWeight="lg">{t('drone.updated')}</Typography></td>
                  <td>{drone.updatedAt ? new Date(drone.updatedAt).toLocaleString() : '-'}</td>
                </tr>
              </tbody>
            </Table>
          </Card>
          <Typography level="h3" mb={1}>{t('drone.last_heartbeat', { id: droneId })}</Typography>
          {droneId && <HeartBeat droneId={droneId as string} />}
        </Container>
      </BreadcrumbLayout>
    </ResponsiveLayout>
  );
};

export default DroneDetails;
